import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Pagination from "@material-ui/lab/Pagination";
import { useGetReservationHistory } from "../../../../utils/react-query/queryReservations/queries";
import type { ReservationHistoryEntry, ReservationHistoryResponse } from "../../types";

interface ReservationHistoryModalProps {
  open: boolean;
  reservationId: number;
  onClose: () => void;
}

const ROWS_PER_PAGE = 10;

const actionLabels: Record<
  ReservationHistoryEntry["action"],
  { label: string; color: "default" | "primary" | "secondary" }
> = {
  created: { label: "Creada", color: "default" },
  edited: { label: "Editada", color: "primary" },
  approved: { label: "Aprobada", color: "primary" },
  rejected: { label: "Rechazada", color: "secondary" },
  finalized: { label: "Finalizada", color: "primary" },
};

function getActionChip(action: ReservationHistoryEntry["action"]) {
  const config = actionLabels[action] ?? { label: action, color: "default" as const };
  return (
    <Chip label={config.label} color={config.color} size="small" variant="outlined" />
  );
}

function renderDetails(
  details: Record<string, unknown> | null,
  action: ReservationHistoryEntry["action"],
): string {
  if (!details) return "—";

  const notes =
    typeof details.notes === "string" && details.notes.trim()
      ? details.notes
      : null;

  switch (action) {
    case "created":
      return notes ? `Notas: ${notes}` : "—";

    case "edited": {
      const parts: string[] = [];
      const items = details.items as Record<string, unknown[]> | undefined;
      if (items) {
        if (items.add?.length) parts.push(`${items.add.length} agregado(s)`);
        if (items.update?.length) parts.push(`${items.update.length} actualizado(s)`);
        if (items.remove?.length) parts.push(`${items.remove.length} eliminado(s)`);
      }
      if (notes) parts.push(`Notas: ${notes}`);
      return parts.length > 0 ? parts.join(", ") : JSON.stringify(details);
    }

    case "approved":
    case "rejected": {
      if (typeof details.reason === "string" && details.reason.trim()) {
        return details.reason;
      }
      return notes ? `Notas: ${notes}` : "—";
    }

    case "finalized":
      return notes ? `Notas: ${notes}` : "—";

    default:
      return JSON.stringify(details);
  }
}

export function ReservationHistoryModal({
  open,
  reservationId,
  onClose,
}: ReservationHistoryModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open) {
      setPage(1);
      setSearchQuery("");
    }
  }, [open, reservationId]);

  const { data, isLoading } = useGetReservationHistory({
    reservation_id: reservationId,
    page,
    limit: ROWS_PER_PAGE,
  });

  const response: ReservationHistoryResponse = useMemo(() => {
    if (!data) {
      return { results: [], total: 0, page: 1, totalPages: 0 };
    }
    if (Array.isArray(data)) {
      return { results: data, total: data.length, page: 1, totalPages: 1 };
    }
    return data;
  }, [data]);

  const results = response.results;

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return results;
    const query = searchQuery.toLowerCase();
    return results.filter((entry) =>
      [
        entry.performed_by_name,
        entry.action,
        renderDetails(entry.details, entry.action),
      ]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(query))
    );
  }, [results, searchQuery]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        disableTypography
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h6">
          Historial de reservas #{reservationId}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <CircularProgress size={32} />
          </div>
        )}

        {!isLoading && results.length === 0 && (
          <Typography variant="body2" color="textSecondary" align="center" style={{ padding: "2rem" }}>
            No hay historial registrado para esta reserva.
          </Typography>
        )}

        {!isLoading && results.length > 0 && (
          <>
            <Box mb={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Buscar por acción, realizado por o detalles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            {filteredResults.length === 0 ? (
              <Typography variant="body2" color="textSecondary" align="center" style={{ padding: "2rem" }}>
                No se encontraron registros con ese criterio de busqueda.
              </Typography>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Fecha</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Acción</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Realizado por</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Detalles</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredResults.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {formatDate(entry.created_at)}
                        </TableCell>
                        <TableCell>{getActionChip(entry.action)}</TableCell>
                        <TableCell>
                          {entry.performed_by_name || (
                            <Typography variant="body2" component="span" color="textSecondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{renderDetails(entry.details, entry.action)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {response.totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={response.totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReservationHistoryModal;
